import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchPerson,
  createPerson,
  updatePerson,
  checkPersonNameExists,
} from "@/api/people";
import { fetchPeopleRoles } from "@/api/peopleRoles";
import { fetchBookSummaries } from "@/data/mockData"; // TODO: Replace with real API when available
import { Person, PeopleRole, BookSummary } from "@/types";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Save,
  X,
  Plus,
  BookOpen,
  Mic,
  Languages,
  User,
  Mail,
  FileText,
  Image,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { MediaPicker } from "@/components/ui";
import { useDebounce } from "@/hooks/useDebounce";

// Type extension for book display with role information
type BookWithRole = BookSummary & {
  personRole: string;
  roleTypes: {
    isAuthor: boolean;
    isNarrator: boolean;
    isTranslator: boolean;
  };
};

// Colors for role badges - match the role indicator icon colors
const getRoleBadgeColor = (roleName: string): string => {
  switch (roleName.toLowerCase()) {
    case "author":
      return "bg-blue-500/10 text-blue-600";
    case "narrator":
      return "bg-green-500/10 text-green-600";
    case "translator":
      return "bg-amber-500/10 text-amber-600";
    default:
      return "bg-primary/10 text-primary";
  }
};

const PersonForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isNewPerson = !id || id === "new";
  const title = isNewPerson ? "Add Person" : "Edit Person";
  const personId = isNewPerson ? null : parseInt(id!, 10);

  // Default person state
  const defaultPerson: Omit<Person, "id" | "createdAt" | "updatedAt"> = {
    name: "",
    email: "",
    bio: "",
    avatarUrl: "",
    roles: [],
    associatedBooks: [],
  };

  // Form state
  const [person, setPerson] =
    useState<Omit<Person, "id" | "createdAt" | "updatedAt">>(defaultPerson);
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    roles: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [bookAssociations, setBookAssociations] = useState<BookWithRole[]>([]);
  const [checkingName, setCheckingName] = useState(false);

  // Debounce name for uniqueness check
  const debouncedName = useDebounce(person.name, 500);

  // Fetch person data if editing
  const { data: personData, isLoading: isLoadingPerson } = useQuery({
    queryKey: ["person", personId],
    queryFn: () => (personId ? fetchPerson(personId) : null),
    enabled: !isNewPerson && !!personId,
  });

  // Fetch roles for selection
  const { data: roles = [] } = useQuery({
    queryKey: ["peopleRoles"],
    queryFn: fetchPeopleRoles,
  });

  // Fetch books for selection
  const { data: booksData } = useQuery({
    queryKey: ["books"],
    queryFn: () => fetchBookSummaries({ page: 1, pageSize: 1000 }),
  });

  const books = useMemo(() => booksData?.data || [], [booksData?.data]);

  // If editing, set form data from fetched person
  useEffect(() => {
    if (!isNewPerson && personData) {
      const { id, createdAt, updatedAt, ...formData } = personData;
      setPerson(formData);
    }
  }, [isNewPerson, personData]);

  // Load book associations whenever person or books data changes
  useEffect(() => {
    if (
      !person.associatedBooks ||
      person.associatedBooks.length === 0 ||
      !books.length
    ) {
      setBookAssociations([]);
      return;
    }

    const loadBookRoles = async () => {
      if (!personId) return;

      // Get author associations
      const { data: authorBooks } = await supabase
        .from("books_authors")
        .select("book_id")
        .eq("person_id", personId);

      // Get narrator associations
      const { data: narratorBooks } = await supabase
        .from("summary_contents")
        .select("book_id")
        .eq("audio_narrator", personId);

      // Get translator associations
      const { data: translatorBooks } = await supabase
        .from("summary_contents")
        .select("book_id")
        .eq("translator", personId);

      // Build association data
      const associations: BookWithRole[] = [];

      person.associatedBooks.forEach((bookId) => {
        const book = books.find((b) => b.id === bookId);
        if (!book) return;

        const isAuthor =
          authorBooks?.some((item) => item.book_id === bookId) || false;
        const isNarrator =
          narratorBooks?.some((item) => item.book_id === bookId) || false;
        const isTranslator =
          translatorBooks?.some((item) => item.book_id === bookId) || false;

        let roleLabel = "";
        if (isAuthor) roleLabel = "Author";
        if (isNarrator)
          roleLabel = roleLabel ? `${roleLabel}, Narrator` : "Narrator";
        if (isTranslator)
          roleLabel = roleLabel ? `${roleLabel}, Translator` : "Translator";

        associations.push({
          ...book,
          personRole: roleLabel || "Unknown",
          roleTypes: {
            isAuthor,
            isNarrator,
            isTranslator,
          },
        });
      });

      setBookAssociations(associations);
    };

    if (isNewPerson) {
      // For new persons, just add basic book info without roles
      const newAssociations = person.associatedBooks
        .map((bookId) => {
          const book = books.find((b) => b.id === bookId);
          if (!book) return null;

          return {
            ...book,
            personRole: "Book associations are managed from the Books page",
            roleTypes: {
              isAuthor: false,
              isNarrator: false,
              isTranslator: false,
            },
          };
        })
        .filter(Boolean) as BookWithRole[];

      setBookAssociations(newAssociations);
    } else {
      loadBookRoles();
    }
  }, [person.associatedBooks, books, personId, isNewPerson]);

  // Check name uniqueness when name changes
  useEffect(() => {
    const checkName = async () => {
      if (debouncedName.trim().length < 2) return; // Don't check very short names

      setCheckingName(true);
      try {
        const nameExists = await checkPersonNameExists(
          debouncedName,
          personId || undefined
        );

        if (nameExists) {
          setErrors((prev) => ({
            ...prev,
            name: "A person with this name already exists",
          }));
        } else {
          // Only clear the error if it was a duplicate name error
          if (errors.name === "A person with this name already exists") {
            setErrors((prev) => ({
              ...prev,
              name: "",
            }));
          }
        }
      } catch (error) {
        console.error("Error checking name:", error);
      } finally {
        setCheckingName(false);
      }
    };

    if (debouncedName) {
      checkName();
    }
  }, [debouncedName, personId, errors.name]);

  // Handle input changes
  const handleInputChange = (field: string, value: string) => {
    setPerson((prev) => ({ ...prev, [field]: value }));

    // Clear errors when user types
    if (
      field === "name" &&
      errors.name &&
      errors.name !== "A person with this name already exists"
    ) {
      setErrors((prev) => ({ ...prev, name: "" }));
    } else if (field === "email" && errors.email) {
      setErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  // Add role to person
  const handleAddRole = (roleId: string) => {
    const roleToAdd = roles.find((r) => r.id === roleId);
    if (!roleToAdd) return;

    // Check if role already exists
    if (person.roles.some((r) => r.id === roleId)) {
      setShowRoleDialog(false);
      return;
    }

    setPerson((prev) => ({
      ...prev,
      roles: [...prev.roles, roleToAdd],
    }));

    // Clear role error if there is one
    if (errors.roles) {
      setErrors((prev) => ({ ...prev, roles: "" }));
    }

    setShowRoleDialog(false);
  };

  // Remove role from person
  const handleRemoveRole = (roleId: string) => {
    setPerson((prev) => ({
      ...prev,
      roles: prev.roles.filter((r) => r.id !== roleId),
    }));
  };

  // Remove book from person
  const handleRemoveBook = (bookId: number) => {
    toast({
      title: "Cannot remove book association",
      description: "Book associations must be managed from the Books page.",
      variant: "default",
    });
  };

  // Render book role icon with text
  const renderBookRole = (book: BookWithRole) => {
    const { isAuthor, isNarrator, isTranslator } = book.roleTypes;
    const roles = [];

    if (isAuthor)
      roles.push(
        <span
          key="author"
          className="inline-flex items-center text-xs px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700"
        >
          <BookOpen className="h-3 w-3 mr-1" /> Author
        </span>
      );

    if (isNarrator)
      roles.push(
        <span
          key="narrator"
          className="inline-flex items-center text-xs px-1.5 py-0.5 rounded-sm bg-green-100 text-green-700"
        >
          <Mic className="h-3 w-3 mr-1" /> Narrator
        </span>
      );

    if (isTranslator)
      roles.push(
        <span
          key="translator"
          className="inline-flex items-center text-xs px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700"
        >
          <Languages className="h-3 w-3 mr-1" /> Translator
        </span>
      );

    if (roles.length === 0) {
      return (
        <span className="text-xs text-muted-foreground">
          (No specific role)
        </span>
      );
    }

    return <div className="flex flex-wrap gap-1 mt-1">{roles}</div>;
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors = { name: "", email: "", roles: "" };
    let isValid = true;

    if (!person.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (person.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(person.email)) {
      newErrors.email = "Invalid email address";
      isValid = false;
    }

    if (person.roles.length === 0) {
      newErrors.roles = "At least one role is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Validate name uniqueness
  const validateNameUniqueness = async (): Promise<boolean> => {
    try {
      if (!person.name.trim()) return false;

      const nameExists = await checkPersonNameExists(
        person.name,
        personId || undefined
      );

      if (nameExists) {
        setErrors((prev) => ({
          ...prev,
          name: "A person with this name already exists",
        }));
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error checking name uniqueness:", error);
      toast({
        title: "Error",
        description: "Could not verify name uniqueness",
        variant: "destructive",
      });
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Check for duplicate names
      const isNameUnique = await validateNameUniqueness();
      if (!isNameUnique) {
        setIsSaving(false);
        return;
      }

      if (isNewPerson) {
        // Create new person
        await createPerson(person);
        toast({
          title: "Person created",
          description: `${person.name} has been created successfully.`,
        });
      } else if (personId) {
        // Update existing person
        await updatePerson(personId, person);
        toast({
          title: "Person updated",
          description: `${person.name} has been updated successfully.`,
        });
      }

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["people"] });

      // Redirect back to people management
      navigate("/admin/people");
    } catch (error) {
      console.error("Error saving person:", error);

      // Show error message
      let errorMessage = "An error occurred while saving";
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Loading state
  if (!isNewPerson && isLoadingPerson) {
    return (
      <AdminLayout title="Loading...">
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={title}>
      <div className="mb-6">
        <Button
          variant="outline"
          className="flex items-center gap-2"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to People Management
        </Button>
      </div>

      <Card className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <CardTitle>{title}</CardTitle>
            <CardDescription>
              {isNewPerson
                ? "Add a new person such as an author, translator, or narrator."
                : "Update the details of an existing person."}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label
                htmlFor="name"
                className={`flex items-center gap-2 ${
                  errors.name ? "text-destructive" : ""
                }`}
              >
                <User className="h-4 w-4" />
                Name <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="name"
                  value={person.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={errors.name ? "border-destructive pr-8" : "pr-8"}
                  placeholder="Full name"
                />
                {checkingName && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="email"
                className={`flex items-center gap-2 ${
                  errors.email ? "text-destructive" : ""
                }`}
              >
                <Mail className="h-4 w-4" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={person.email || ""}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={errors.email ? "border-destructive" : ""}
                placeholder="Email address"
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="avatarUrl" className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                Person Avatar
              </Label>
              <MediaPicker
                onSelect={(url) => handleInputChange("avatarUrl", url)}
                currentValue={person.avatarUrl || ""}
                allowedTypes={["image/jpeg", "image/jpg", "image/png"]}
                title="Select Person Avatar"
                description="Choose an avatar image for this person from your media library or upload a new one"
                buttonText="Select Avatar Image"
                showPreview={true}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Bio
              </Label>
              <Textarea
                id="bio"
                value={person.bio || ""}
                onChange={(e) => handleInputChange("bio", e.target.value)}
                placeholder="Brief biography or description (optional)"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label
                  className={`flex items-center gap-2 ${
                    errors.roles ? "text-destructive" : ""
                  }`}
                >
                  Roles <span className="text-destructive">*</span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRoleDialog(true)}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Role
                </Button>
              </div>

              {errors.roles && (
                <p className="text-sm text-destructive">{errors.roles}</p>
              )}

              <div className="flex flex-wrap gap-2 p-3 border rounded-md min-h-[60px]">
                {person.roles.length === 0 ? (
                  <div className="flex items-center justify-center w-full h-full text-muted-foreground text-sm">
                    No roles assigned
                  </div>
                ) : (
                  person.roles.map((role) => (
                    <Badge
                      key={role.id}
                      className={getRoleBadgeColor(role.name)}
                      variant="outline"
                    >
                      {role.name}
                      <button
                        type="button"
                        className="ml-2 rounded-full hover:bg-background/20"
                        onClick={() => handleRemoveRole(role.id)}
                      >
                        <X className="h-3 w-3" />
                        <span className="sr-only">Remove {role.name}</span>
                      </button>
                    </Badge>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Associated Books
              </Label>

              <div className="flex flex-col gap-2 p-3 border rounded-md min-h-[60px]">
                {!person.associatedBooks ||
                person.associatedBooks.length === 0 ? (
                  <div className="flex items-center justify-center w-full h-full text-muted-foreground text-sm">
                    No books associated
                  </div>
                ) : (
                  bookAssociations.map((book) => (
                    <div
                      key={book.id}
                      className="flex flex-col p-2 border border-border rounded-md bg-muted/30"
                    >
                      <div className="font-medium">{book.title}</div>
                      {!isNewPerson && renderBookRole(book)}
                    </div>
                  ))
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Book associations are managed from the Books page
              </p>
            </div>
          </CardContent>

          <CardFooter className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving} className="gap-2">
              {isSaving ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {isNewPerson ? "Create Person" : "Update Person"}
                </>
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>

      {/* Role Selection Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Role</DialogTitle>
            <DialogDescription>
              Select a role to assign to {person.name || "this person"}.
            </DialogDescription>
          </DialogHeader>

          <Command>
            <CommandInput placeholder="Search roles..." />
            <CommandList>
              <CommandEmpty>No roles found.</CommandEmpty>
              <CommandGroup>
                {roles
                  .filter((role) => role.isActive)
                  .filter((role) => !person.roles.some((r) => r.id === role.id))
                  .map((role) => (
                    <CommandItem
                      key={role.id}
                      onSelect={() => handleAddRole(role.id)}
                      className="cursor-pointer"
                    >
                      <Badge
                        className={cn(getRoleBadgeColor(role.name), "mr-2")}
                        variant="outline"
                      >
                        {role.name}
                      </Badge>
                      {role.description && (
                        <span className="text-muted-foreground text-sm ml-2">
                          {role.description}
                        </span>
                      )}
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default PersonForm;
